import React, {useContext, useCallback, useMemo} from 'react';
import {useNavigate, NavLink} from 'react-router-dom';
import {darken} from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {signOut} from 'firebase/auth';

import {auth} from '-/firebase';

import {UserContext} from '-/contexts/User';
import {SettingsContext} from '-/contexts/Settings';

import {MENU_GROUPS} from './menuConfig';

import DevModeAlert from './DevModeAlert';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Collapse from '@mui/material/Collapse';

const ImpersonatingAlert = () => {
    const impersonating = localStorage.getItem('impersonating');
    const navigate = useNavigate();
    const {currentUser} = useContext(UserContext);
    const {fullName, email} = currentUser || {};

    if (!impersonating) {
        return;
    }

    const onLogOut = async() => {
        await signOut(auth);
        localStorage.removeItem('impersonating');

        navigate('/login');
    };

    return (
        <Alert
            size="small"
            severity="error"
            icon={false}
            sx={{m: 1, mt: 0, fontSize: 12, px: 1, py: 0, alignItems: 'center'}}
        >
            <Stack direction="column" spacing={1} sx={{alignItems: 'stretch'}}>
                <Typography variant="body2">
                    Impersonating <strong>{fullName || email}</strong>
                </Typography>
                <Button color="inherit" size="small" onClick={onLogOut}>
                    Logout
                </Button>
            </Stack>
        </Alert>
    );
};

export default function MenuContent({toggleDrawer, allItems, preferences}) {
    const navigate = useNavigate();
    const {dispatchStreamLink} = useContext(SettingsContext);
    const {
        shortcuts, expandedGroups, loaded,
        addShortcut, removeShortcut, toggleGroup
    } = preferences;

    const shortcutSet = useMemo(() => new Set(shortcuts), [shortcuts]);

    const shortcutItems = useMemo(() => {
        return shortcuts
            .map(id => allItems.find(item => item.id === id))
            .filter(Boolean);
    }, [shortcuts, allItems]);

    const groupedItems = useMemo(() => {
        const groups = {};
        for (const item of allItems) {
            if (!groups[item.group]) {
                groups[item.group] = [];
            }
            groups[item.group].push(item);
        }
        return groups;
    }, [allItems]);

    const activeGroups = useMemo(() => {
        return MENU_GROUPS.filter(g => groupedItems[g.id]?.length > 0);
    }, [groupedItems]);

    const handleClick = useCallback((e, item) => {
        if (toggleDrawer) {
            toggleDrawer();
        }

        const {to} = item;
        const {metaKey} = e;
        if (metaKey) {
            window.open(to);
            return;
        }

        navigate(to);
    }, [navigate, toggleDrawer]);

    const handleDispatchStream = useCallback(() => {
        if (!dispatchStreamLink) {
            return;
        }

        window.open(dispatchStreamLink, '_blank');
    }, [dispatchStreamLink]);

    if (!loaded) {
        return null;
    }

    return (
        <>
            <Stack sx={{flexGrow: 1, p: 1, justifyContent: 'space-between'}}>
                <Box>
                    {/* Shortcuts section */}
                    <List className="menu-nav" dense sx={{p: 0}}>
                        {shortcutItems.map(item => (
                            <Box
                                key={item.id}
                                sx={{
                                    position: 'relative',
                                    // Touch devices have no hover: keep the action visible at a low
                                    // opacity instead of revealing it on hover. A hover-triggered
                                    // visibility change makes iOS Safari swallow the first tap, so
                                    // links would need two taps to navigate (#826).
                                    '& .shortcut-hide': {opacity: 0.5, transition: 'opacity 0.15s'},
                                    '@media (hover: hover)': {
                                        '& .shortcut-hide': {opacity: 0},
                                        '&:hover .shortcut-hide': {opacity: 0.5},
                                        '& .shortcut-hide:hover': {opacity: 1}
                                    }
                                }}
                            >
                                <NavLink style={{textDecoration: 'none', color: 'rgba(0, 0, 0, 0.87)'}} to={item.to}>
                                    {({isActive}) => (
                                        <ListItem disablePadding>
                                            <ListItemButton
                                                selected={isActive}
                                                onClick={e => handleClick(e, item)}
                                                sx={{pr: {xs: 2, sm: 5}, minHeight: 48, '&& .MuiListItemText-primary': {fontSize: '1rem'}}}
                                            >
                                                <ListItemIcon>{item.icon}</ListItemIcon>
                                                <ListItemText primary={item.label} />
                                            </ListItemButton>
                                        </ListItem>
                                    )}
                                </NavLink>
                                <IconButton
                                    className="shortcut-hide"
                                    size="small"
                                    onClick={() => removeShortcut(item.id)}
                                    sx={{position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', display: {xs: 'none', sm: 'inline-flex'}}}
                                >
                                    <VisibilityOffIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                    </List>

                    {/* Grouped sections */}
                    <Stack sx={{mt: 1, gap: 1}}>
                        {activeGroups.map(group => {
                            const items = groupedItems[group.id] || [];
                            const isExpanded = expandedGroups.includes(group.id);

                            return (
                                <Box
                                    key={group.id}
                                    sx={theme => ({
                                        bgcolor: darken(theme.palette.background.default, 0.01),
                                        borderRadius: 1,
                                        overflow: 'hidden'
                                    })}
                                >
                                    <ListItemButton
                                        onClick={() => toggleGroup(group.id)}
                                        dense
                                        sx={{minHeight: 48}}
                                    >
                                        <ListItemText
                                            primary={group.label}
                                            primaryTypographyProps={{variant: 'overline', sx: {opacity: 0.5}}}
                                        />
                                        <ExpandMoreIcon fontSize="small" color="action" sx={{transition: 'transform 0.2s', transform: isExpanded ? 'rotate(-180deg)' : 'rotate(0deg)'}} />
                                    </ListItemButton>

                                    <Collapse in={isExpanded}>
                                        <List className="menu-nav" dense sx={{pt: 0}}>
                                            {items.map(item => {
                                                const isShortcut = shortcutSet.has(item.id);

                                                return (
                                                    <Box
                                                        key={item.id}
                                                        sx={{
                                                            position: 'relative',
                                                            // Touch devices have no hover: keep the pin visible at a
                                                            // constant opacity. A hover-reveal here makes iOS Safari
                                                            // swallow the first tap on the link (#826).
                                                            '& .pin-action': {opacity: 0.4, transition: 'opacity 0.15s'},
                                                            '@media (hover: hover)': {
                                                                '& .pin-action': {opacity: isShortcut ? 0.4 : 0},
                                                                '&:hover .pin-action': {opacity: isShortcut ? 0.4 : 0.3},
                                                                '& .pin-action:hover': {opacity: 1}
                                                            }
                                                        }}
                                                    >
                                                        <NavLink style={{textDecoration: 'none', color: 'rgba(0, 0, 0, 0.87)'}} to={item.to}>
                                                            {({isActive}) => (
                                                                <ListItem disablePadding>
                                                                    <ListItemButton
                                                                        selected={isActive}
                                                                        onClick={e => handleClick(e, item)}
                                                                        sx={{pr: {xs: 2, sm: 5}, minHeight: 48, '&& .MuiListItemText-primary': {fontSize: '1rem'}}}
                                                                    >
                                                                        <ListItemIcon>{item.icon}</ListItemIcon>
                                                                        <ListItemText primary={item.label} />
                                                                    </ListItemButton>
                                                                </ListItem>
                                                            )}
                                                        </NavLink>
                                                        <IconButton
                                                            className="pin-action"
                                                            size="small"
                                                            onClick={() => isShortcut ? removeShortcut(item.id) : addShortcut(item.id)}
                                                            sx={{position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', display: {xs: 'none', sm: 'inline-flex'}}}
                                                        >
                                                            {isShortcut
                                                                ? <PushPinIcon fontSize="small" />
                                                                : <PushPinOutlinedIcon fontSize="small" />}
                                                        </IconButton>
                                                    </Box>
                                                );
                                            })}
                                        </List>
                                    </Collapse>
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>

                {!!dispatchStreamLink && (
                    <Button
                        variant="outlined"
                        size="small"
                        endIcon={<OpenInNewIcon />}
                        sx={{mx: 1, mb: 1, mt: 1}}
                        onClick={handleDispatchStream}
                    >
                        Open Dispatch Stream
                    </Button>
                )}
            </Stack>

            <ImpersonatingAlert />
            <DevModeAlert />
        </>
    );
}
